import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useGuardaCanvis, useHiHaCanvis } from '@/pages/administracio/marc/canvisContext';
import { useTranslation } from 'react-i18next';
import { useActivacions, useTauler } from '@/api/activacions';
import { useSollicituds } from '@/api/planificacio';
import { useAuth } from '@/auth/AuthContext';
import { Icona } from '@/components/Icona';
import { Logotip } from '@/components/Logotip';
import { config } from '@/config/env';
import { IDIOMES } from '@/i18n';
import { desaIdiomaPerfil } from '@/i18n/preferencies';
import { useIdioma } from '@/i18n/useFormat';
import { EditaTelefon } from './TelefonMobil';
import {
  rutaSeccio,
  SECCIONS,
  seccioDeRuta,
  type Seccio,
  type SeccioNav,
} from '@/navigation/navigation';
import { useMe } from '@/permisos/MeContext';

function inicials(nom: string): string {
  return nom
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');
}

interface Comptador {
  valor: number;
  vermell?: boolean;
}

/**
 * Comptadors de la barra lateral: activacions obertes (vermell només si n'hi ha de crítiques)
 * i canvis de torn pendents a Guàrdies.
 */
function useComptadors(): Partial<Record<SeccioNav, Comptador>> {
  const { pot } = useMe();
  const veuActivacions = pot('cai.activacions', 'CONSULTAR');
  const tauler = useTauler(veuActivacions);
  const critiques = useActivacions({ critica: true, estat: 'OBERTA', size: 1 }, veuActivacions);
  const sollicituds = useSollicituds('PENDENT', pot('cai.sollicituds', 'CONSULTAR'));
  const obertes = tauler.data?.obertes ?? 0;
  const pendents = sollicituds.data?.length ?? 0;
  return {
    activacions:
      obertes > 0 ? { valor: obertes, vermell: (critiques.data?.total ?? 0) > 0 } : undefined,
    guardies: pendents > 0 ? { valor: pendents } : undefined,
  };
}

function ItemSeccio({
  seccio,
  ruta,
  activa,
  comptador,
}: {
  seccio: Seccio;
  ruta: string;
  activa: boolean;
  comptador?: Comptador;
}) {
  const { t } = useTranslation('comu');
  const text = t(`nav.seccions.${seccio.clau}`);
  const navigate = useNavigate();
  const guarda = useGuardaCanvis();
  const canvis = useHiHaCanvis();
  return (
    <Link
      to={ruta}
      onClick={(e) => {
        // Amb canvis sense desar a Ajustos, es demana confirmació abans de sortir.
        if (!canvis || e.metaKey || e.ctrlKey || e.shiftKey) return;
        e.preventDefault();
        guarda(() => navigate(ruta));
      }}
      title={text}
      data-seccio={seccio.clau}
      aria-current={activa ? 'page' : undefined}
      className={`side-item${activa ? ' is-active' : ''}`}
    >
      <Icona nom={seccio.icona} />
      <span className="side-item__text">{text}</span>
      {comptador ? (
        <span
          className={`side-item__badge${comptador.vermell ? ' side-item__badge--red' : ''}`}
          data-testid={`comptador-${seccio.clau}`}
        >
          {comptador.valor}
        </span>
      ) : null}
    </Link>
  );
}

function MenuUsuari() {
  const { t } = useTranslation('comu');
  const { me } = useMe();
  const { logout, compte, mode } = useAuth();
  const idioma = useIdioma();
  const [obert, setObert] = useState(false);
  const [editaMobil, setEditaMobil] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const nom = me ? `${me.nom} ${me.cognoms ?? ''}`.trim() : (compte?.nom ?? '');
  const rol = me?.rols[0]?.nom ?? me?.departament ?? compte?.upn ?? '';

  useEffect(() => {
    if (!obert) return;
    const fora = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setObert(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setObert(false);
    };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', esc);
    };
  }, [obert]);

  return (
    <div className="sidebar__foot" ref={ref}>
      {obert ? (
        <div className="menu" role="menu" aria-label={t('sessio.menu')}>
          <div className="menu__titol">{t('idioma.etiqueta')}</div>
          {IDIOMES.map((i) => (
            <button
              key={i}
              type="button"
              role="menuitemradio"
              aria-checked={idioma === i}
              className="menu__item"
              onClick={() => {
                void desaIdiomaPerfil(i, mode);
                setObert(false);
              }}
            >
              <Icona nom={idioma === i ? 'checkmark' : 'globe'} />
              {t(`idioma.${i}`)}
            </button>
          ))}
          <div className="menu__sep" />
          <button
            type="button"
            role="menuitem"
            className="menu__item"
            onClick={() => {
              setEditaMobil(true);
              setObert(false);
            }}
          >
            <Icona nom="phone" />
            {t('sessio.elMeuMobil')}
          </button>
          {mode === 'msal' ? (
            <button
              type="button"
              role="menuitem"
              className="menu__item"
              onClick={() => void logout()}
            >
              <Icona nom="person-2" />
              {t('sessio.canviaCompte')}
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            className="menu__item menu__item--perill"
            onClick={() => void logout()}
          >
            <Icona nom="logout" />
            {t('sessio.tanca')}
          </button>
        </div>
      ) : null}
      <button
        type="button"
        className="side-item sidebar__user"
        aria-haspopup="menu"
        aria-expanded={obert}
        title={nom}
        onClick={() => setObert((o) => !o)}
      >
        <span className="avatar">{inicials(nom)}</span>
        <div className="truncate">
          <div className="sidebar__user-nom truncate">{nom}</div>
          <div className="sidebar__user-rol truncate">{rol}</div>
        </div>
        <Icona nom="chevron-updown" />
      </button>
      {editaMobil ? <EditaTelefon onTanca={() => setEditaMobil(false)} /> : null}
    </div>
  );
}

/**
 * Barra lateral del redisseny: logotip, les seccions del dia a dia (Centre de control,
 * Activacions, Guàrdies, Anàlisi), Ajustos fixat a baix i el bloc d'usuari. Cada secció
 * només apareix si l'usuari té permís almenys sobre una de les seves pantalles.
 */
export function Sidebar() {
  const { t } = useTranslation('comu');
  const { me } = useMe();
  const { pathname } = useLocation();
  const comptadors = useComptadors();
  const actual = seccioDeRuta(pathname);
  const item = (s: Seccio) => {
    const ruta = rutaSeccio(me, s);
    if (!ruta) return null;
    return (
      <ItemSeccio
        key={s.clau}
        seccio={s}
        ruta={ruta}
        activa={actual === s.clau}
        comptador={comptadors[s.clau]}
      />
    );
  };

  return (
    <nav className="sidebar" aria-label={t('nav.principal')}>
      <div className="sidebar__brand">
        <Logotip className="sidebar__logo" />
        <div>
          <div className="sidebar__name">{t('app.logo')}</div>
          <div className="sidebar__sub">{t('app.organitzacio', { entorn: config.entorn })}</div>
        </div>
      </div>
      {SECCIONS.filter((s) => !s.peu).map(item)}
      <div className="sidebar__espai" />
      {SECCIONS.filter((s) => s.peu).map(item)}
      <MenuUsuari />
    </nav>
  );
}
